import levelsData from "../generated/levels.json" with { type: "json" };
import { BruteforceSessionController } from "./bruteforce/session-controller.js";
import { FRAME_RATE, STAGE_HEIGHT, STAGE_WIDTH } from "./core/constants.js";
import { FixedStepLoop } from "./core/fixed-step-loop.js";
import { convertEventsToLegacyInput, convertLegacyInputToEvents } from "./core/input-format-converter.js";
import { ScriptedInputState } from "./core/scripted-input.js";
import { SaveStore } from "./core/storage.js";
import { GameSession } from "./game/game-session.js";

const TEMPORARILY_LOCKED_LEVEL_IDS = new Set([9, 13, 14]);

export class App {
  constructor(root) {
    this.root = root;
    this.store = new SaveStore();
    this.input = new ScriptedInputState(window);
    this.session = null;
    this.simulationFps = FRAME_RATE;
    this.zoomLevel = Math.min(4, Math.max(0.1, Number(this.store.state.cameraZoom) || 1));
    this.hitboxStrokeWidth = Math.min(2, Math.max(0.1, Number(this.store.state.hitboxStrokeWidth) || 2));
    this.freeCameraEnabled = false;
    this.handleResize = () => this.updateStageScale();
    this.bruteforceSessions = [];
    this.nextBruteforceSessionId = 1;
    this.buildUI();
    window.addEventListener("resize", this.handleResize);
    const url = new URL(window.location.href);
    const directLevel = Number(url.searchParams.get("level") || 0);
    if (directLevel >= 1 && directLevel <= 17) {
      this.startGame(directLevel);
    } else {
      this.showMenu();
    }
    this.restoreBruteforceSessions();
    this.loop = new FixedStepLoop({
      stepHz: FRAME_RATE,
      getStepScale: () => this.simulationFps / FRAME_RATE,
      update: () => {
        if (this.session) {
          this.input.syncFrame(this.session.getRaceFrame());
        }
        this.session?.update();
        this.input.beginFrame();
      },
      render: (deltaMs) => {
        this.session?.updateFreeCamera(deltaMs);
        this.session?.render();
        this.updateStats();
      },
    });
    this.loop.start();
  }

  buildUI() {
    this.root.innerHTML = "";
    this.shell = document.createElement("div");
    this.shell.className = "shell";

    this.topBar = document.createElement("div");
    this.topBar.className = "top-bar";

    const createPanel = (titleText, className = "") => {
      const panel = document.createElement("section");
      panel.className = `control-panel${className ? ` ${className}` : ""}`;

      const title = document.createElement("span");
      title.className = "control-panel-title";
      title.textContent = titleText;

      const body = document.createElement("div");
      body.className = "control-panel-body";

      panel.append(title, body);
      return { panel, body };
    };

    const playbackPanel = createPanel("Playback", "control-panel-playback");
    const displayPanel = createPanel("Display & Rules", "control-panel-toggles");
    const telemetryPanel = createPanel("Telemetry", "control-panel-telemetry");

    const speedGroup = document.createElement("div");
    speedGroup.className = "top-group top-group-slider";

    const speedLabel = document.createElement("span");
    speedLabel.className = "top-label";
    speedLabel.textContent = "Speed";

    this.speedSlider = document.createElement("input");
    this.speedSlider.type = "range";
    this.speedSlider.min = "0";
    this.speedSlider.max = "93";
    this.speedSlider.step = "1";
    this.speedSlider.value = String(FRAME_RATE);
    this.speedSlider.addEventListener("input", () => {
      this.simulationFps = Number(this.speedSlider.value);
      this.speedValue.textContent = `${this.simulationFps} fps`;
      this.syncStepFrameControl();
    });

    this.speedValue = document.createElement("span");
    this.speedValue.className = "top-value";
    this.speedValue.textContent = `${FRAME_RATE} fps`;
    speedGroup.append(speedLabel, this.speedSlider, this.speedValue);

    const zoomGroup = document.createElement("div");
    zoomGroup.className = "top-group top-group-slider";

    const zoomLabel = document.createElement("span");
    zoomLabel.className = "top-label";
    zoomLabel.textContent = "Zoom";

    this.zoomSlider = document.createElement("input");
    this.zoomSlider.type = "range";
    this.zoomSlider.min = "0.1";
    this.zoomSlider.max = "4";
    this.zoomSlider.step = "0.1";
    this.zoomSlider.value = String(this.zoomLevel);
    this.zoomSlider.addEventListener("input", () => {
      this.zoomLevel = Math.min(4, Math.max(0.1, Number(this.zoomSlider.value) || 1));
      this.zoomValue.textContent = `${this.zoomLevel.toFixed(1)}x`;
      this.store.state.cameraZoom = this.zoomLevel;
      this.store.write();
      this.session?.setZoom(this.zoomLevel);
    });

    this.zoomValue = document.createElement("span");
    this.zoomValue.className = "top-value";
    this.zoomValue.textContent = `${this.zoomLevel.toFixed(1)}x`;
    zoomGroup.append(zoomLabel, this.zoomSlider, this.zoomValue);

    const hitboxStrokeGroup = document.createElement("div");
    hitboxStrokeGroup.className = "top-group top-group-slider";

    const hitboxStrokeLabel = document.createElement("span");
    hitboxStrokeLabel.className = "top-label";
    hitboxStrokeLabel.textContent = "Hitbox Stroke";

    this.hitboxStrokeSlider = document.createElement("input");
    this.hitboxStrokeSlider.type = "range";
    this.hitboxStrokeSlider.min = "0.1";
    this.hitboxStrokeSlider.max = "2";
    this.hitboxStrokeSlider.step = "0.1";
    this.hitboxStrokeSlider.value = String(this.hitboxStrokeWidth);
    this.hitboxStrokeSlider.addEventListener("input", () => {
      this.hitboxStrokeWidth = Math.min(2, Math.max(0.1, Number(this.hitboxStrokeSlider.value) || 2));
      this.hitboxStrokeValue.textContent = `${this.hitboxStrokeWidth.toFixed(1)}px`;
      this.store.state.hitboxStrokeWidth = this.hitboxStrokeWidth;
      this.store.write();
      this.session?.setHitboxStrokeWidth(this.hitboxStrokeWidth);
    });

    this.hitboxStrokeValue = document.createElement("span");
    this.hitboxStrokeValue.className = "top-value";
    this.hitboxStrokeValue.textContent = `${this.hitboxStrokeWidth.toFixed(1)}px`;
    hitboxStrokeGroup.append(hitboxStrokeLabel, this.hitboxStrokeSlider, this.hitboxStrokeValue);

    const createStat = (labelText) => {
      const stat = document.createElement("div");
      stat.className = "top-group";
      const label = document.createElement("span");
      label.className = "top-label";
      label.textContent = labelText;
      const value = document.createElement("span");
      value.className = "top-value";
      value.textContent = "--";
      stat.append(label, value);
      return { stat, value };
    };

    const frameStat = createStat("Frame");
    this.frameValue = frameStat.value;
    const vxStat = createStat("VX");
    this.vxValue = vxStat.value;
    const vyStat = createStat("VY");
    this.vyValue = vyStat.value;
    const xStat = createStat("X");
    this.xValue = xStat.value;
    const yStat = createStat("Y");
    this.yValue = yStat.value;
    const rotationStat = createStat("Rot");
    this.rotationValue = rotationStat.value;
    const rotationVelocityStat = createStat("Rot Vel");
    this.rotationVelocityValue = rotationVelocityStat.value;

    const createToggle = (labelText, initialValue, onChange, valueText = "Show") => {
      const group = document.createElement("label");
      group.className = "top-group top-group-toggle";
      const label = document.createElement("span");
      label.className = "top-label";
      label.textContent = labelText;
      const row = document.createElement("span");
      row.className = "top-toggle-row";
      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.checked = initialValue;
      checkbox.addEventListener("change", () => onChange(checkbox.checked));
      const value = document.createElement("span");
      value.className = "top-value";
      value.textContent = valueText;
      row.append(checkbox, value);
      group.append(label, row);
      return { group, checkbox };
    };

    const finishHitboxToggle = createToggle("Finish Hitbox", Boolean(this.store.state.showFinishHitbox), (checked) => {
      this.store.state.showFinishHitbox = checked;
      this.store.write();
      this.session?.setShowFinishHitbox(checked);
    });
    this.finishHitboxCheckbox = finishHitboxToggle.checkbox;

    const ballHitboxToggle = createToggle("Ball Hitbox", Boolean(this.store.state.showBallHitbox), (checked) => {
      this.store.state.showBallHitbox = checked;
      this.store.write();
      this.session?.setShowBallHitbox(checked);
    });
    this.ballHitboxCheckbox = ballHitboxToggle.checkbox;

    const autoAdvanceToggle = createToggle("Auto Next Level", this.store.state.autoAdvanceLevel !== false, (checked) => {
      this.store.state.autoAdvanceLevel = checked;
      this.store.write();
      this.session?.setAutoAdvanceLevel(checked);
    }, "On");
    this.autoAdvanceCheckbox = autoAdvanceToggle.checkbox;

    const spikesKillToggle = createToggle("Spikes Kill", this.store.state.spikesKill === true, (checked) => {
      this.store.state.spikesKill = checked;
      this.store.write();
      this.session?.setSpikesKill(checked);
    }, "On");
    this.spikesKillCheckbox = spikesKillToggle.checkbox;

    const fixedTransitionTimingToggle = createToggle("Fixed Transition", this.store.state.fixedLevelTransitionTiming === true, (checked) => {
      this.store.state.fixedLevelTransitionTiming = checked;
      this.store.write();
      this.session?.setFixedLevelTransitionTiming(checked);
    }, "2.871s");
    this.fixedTransitionTimingCheckbox = fixedTransitionTimingToggle.checkbox;

    const tasDeathWarpToggle = createToggle("TAS Death Warp", this.store.state.tasDeathWarp === true, (checked) => {
      this.store.state.tasDeathWarp = checked;
      this.store.write();
      this.session?.setTasDeathWarp(checked);
    }, "L4/L9");
    this.tasDeathWarpCheckbox = tasDeathWarpToggle.checkbox;

    const stepGroup = document.createElement("div");
    stepGroup.className = "top-group top-group-button";
    this.stepGroup = stepGroup;
    const stepLabel = document.createElement("span");
    stepLabel.className = "top-label";
    stepLabel.textContent = "Step";
    this.stepFrameButton = document.createElement("button");
    this.stepFrameButton.type = "button";
    this.stepFrameButton.className = "btn btn-small";
    this.stepFrameButton.textContent = "Advance Frame";
    this.stepFrameButton.addEventListener("click", () => this.advanceOneFrame());
    stepGroup.append(stepLabel, this.stepFrameButton);

    const freeCameraGroup = document.createElement("div");
    freeCameraGroup.className = "top-group top-group-button";
    const freeCameraLabel = document.createElement("span");
    freeCameraLabel.className = "top-label";
    freeCameraLabel.textContent = "Camera";
    this.freeCameraButton = document.createElement("button");
    this.freeCameraButton.type = "button";
    this.freeCameraButton.className = "btn btn-small";
    this.freeCameraButton.addEventListener("click", () => this.toggleFreeCamera());
    freeCameraGroup.append(freeCameraLabel, this.freeCameraButton);

    playbackPanel.body.append(speedGroup, zoomGroup, hitboxStrokeGroup, stepGroup, freeCameraGroup);
    displayPanel.body.append(
      finishHitboxToggle.group,
      ballHitboxToggle.group,
      autoAdvanceToggle.group,
      spikesKillToggle.group,
      fixedTransitionTimingToggle.group,
      tasDeathWarpToggle.group,
    );
    telemetryPanel.body.append(frameStat.stat, vxStat.stat, vyStat.stat, xStat.stat, yStat.stat, rotationStat.stat, rotationVelocityStat.stat);

    this.topBar.append(displayPanel.panel, telemetryPanel.panel);
    this.shell.appendChild(this.topBar);

    this.frameViewport = document.createElement("div");
    this.frameViewport.className = "frame-viewport";
    this.frame = document.createElement("div");
    this.frame.className = "frame";
    this.canvas = document.createElement("canvas");
    this.canvas.width = STAGE_WIDTH;
    this.canvas.height = STAGE_HEIGHT;
    this.canvas.style.width = `${STAGE_WIDTH}px`;
    this.canvas.style.height = `${STAGE_HEIGHT}px`;
    this.hudLayer = document.createElement("div");
    this.hudLayer.className = "hud-layer";
    this.overlayLayer = document.createElement("div");
    this.overlayLayer.className = "overlay-layer";
    this.frame.append(this.canvas, this.hudLayer, this.overlayLayer);
    this.frameViewport.appendChild(this.frame);

    this.mainWorkspace = document.createElement("div");
    this.mainWorkspace.className = "main-workspace";
    this.leftRail = document.createElement("div");
    this.leftRail.className = "left-rail";
    this.playColumn = document.createElement("div");
    this.playColumn.className = "play-column";
    this.playColumn.appendChild(this.frameViewport);

    this.sideColumn = document.createElement("div");
    this.sideColumn.className = "side-column";

    this.inputPanel = document.createElement("div");
    this.inputPanel.className = "input-panel input-panel-side";

    const createInputColumn = (title, readOnly) => {
      const column = document.createElement("label");
      column.className = "input-column";
      const heading = document.createElement("span");
      heading.className = "input-title";
      heading.textContent = title;
      const field = document.createElement("textarea");
      field.className = "script-field";
      field.readOnly = readOnly;
      field.spellcheck = false;
      column.append(heading, field);
      return { column, field };
    };

    const recordedColumn = createInputColumn("Recorded Inputs", true);
    const botColumn = createInputColumn("Bot Inputs", false);
    botColumn.field.placeholder = "0:25 press up\n0:30 rel up";

    this.input.attachPanels({
      recordField: recordedColumn.field,
      scriptField: botColumn.field,
      initialScript: this.store.state.botInputScript ?? "",
      onScriptChange: (value) => {
        this.store.state.botInputScript = value;
        this.store.write();
      },
    });

    this.inputPanel.append(recordedColumn.column, botColumn.column);
    this.sideColumn.appendChild(this.inputPanel);

    const converterPanel = document.createElement("section");
    converterPanel.className = "converter-panel";

    const converterHeader = document.createElement("div");
    converterHeader.className = "converter-heading";
    const converterTitle = document.createElement("span");
    converterTitle.className = "input-title";
    converterTitle.textContent = "Input Converter";
    converterHeader.append(converterTitle);

    this.converterError = document.createElement("div");
    this.converterError.className = "converter-error";
    this.converterError.hidden = true;

    this.converterField = document.createElement("textarea");
    this.converterField.className = "converter-input";
    this.converterField.spellcheck = false;
    this.converterField.placeholder = "Paste either legacy input or timestamped events here";

    const converterActions = document.createElement("div");
    converterActions.className = "converter-actions";

    this.convertInputButton = document.createElement("button");
    this.convertInputButton.type = "button";
    this.convertInputButton.className = "btn btn-small";
    this.convertInputButton.textContent = `Convert (${FRAME_RATE} fps)`;
    this.convertInputButton.addEventListener("click", () => this.convertInputFormat());

    converterActions.append(this.convertInputButton);

    converterPanel.append(converterHeader, this.converterField, converterActions, this.converterError);
    this.sideColumn.appendChild(converterPanel);

    this.bruteforceWorkspace = document.createElement("div");
    this.bruteforceWorkspace.className = "bruteforce-workspace";

    const actionsPanel = document.createElement("section");
    actionsPanel.className = "bruteforce-actions-panel";
    const actionsHeading = document.createElement("div");
    actionsHeading.className = "bruteforce-heading";
    const actionsTitle = document.createElement("span");
    actionsTitle.className = "input-title";
    actionsTitle.textContent = "Bruteforce Sessions";
    const actionsCopy = document.createElement("p");
    actionsCopy.className = "bruteforce-copy";
    actionsCopy.textContent = "Create detached bruteforce sessions that run independently from the live game.";
    actionsHeading.append(actionsTitle, actionsCopy);
    this.addBruteforceSessionButton = document.createElement("button");
    this.addBruteforceSessionButton.type = "button";
    this.addBruteforceSessionButton.className = "btn";
    this.addBruteforceSessionButton.textContent = "Add Bruteforce Session";
    this.addBruteforceSessionButton.addEventListener("click", () => this.addBruteforceSession());
    actionsPanel.append(actionsHeading, this.addBruteforceSessionButton);

    this.bruteforceSessionList = document.createElement("div");
    this.bruteforceSessionList.className = "bruteforce-session-list";
    this.bruteforceEmptyState = document.createElement("p");
    this.bruteforceEmptyState.className = "bruteforce-empty";
    this.bruteforceEmptyState.textContent = "No bruteforce sessions yet.";

    this.leftRail.append(playbackPanel.panel, actionsPanel);
    this.bruteforceWorkspace.append(this.bruteforceEmptyState, this.bruteforceSessionList);
    this.mainWorkspace.append(this.leftRail, this.playColumn, this.sideColumn, this.bruteforceWorkspace);
    this.shell.appendChild(this.mainWorkspace);
    this.root.appendChild(this.shell);

    this.syncStepFrameControl();
    this.syncFreeCameraControl();
    this.updateStageScale();
    this.updateStats();
  }

  syncFreeCameraControl() {
    if (!this.freeCameraButton) {
      return;
    }
    this.freeCameraButton.textContent = this.freeCameraEnabled ? "Disable Free Cam" : "Enable Free Cam";
  }

  toggleFreeCamera() {
    this.freeCameraEnabled = !this.freeCameraEnabled;
    this.session?.setFreeCameraEnabled(this.freeCameraEnabled);
    this.syncFreeCameraControl();
  }

  updateStageScale() {
    if (!this.frameViewport || !this.frame) {
      return;
    }
    const horizontalPadding = 24;
    const verticalPadding = 24;
    const workspaceStyles = this.mainWorkspace ? window.getComputedStyle(this.mainWorkspace) : null;
    const workspaceColumns = workspaceStyles?.gridTemplateColumns.split(" ").filter(Boolean) ?? [];
    const workspaceGap = workspaceStyles ? Number.parseFloat(workspaceStyles.columnGap || workspaceStyles.gap || "0") : 0;
    let availableWidth = this.mainWorkspace?.getBoundingClientRect().width ?? (window.innerWidth - horizontalPadding);
    if (workspaceColumns.length > 1) {
      const leftWidth = this.leftRail?.getBoundingClientRect().width ?? 0;
      const rightWidth = this.sideColumn?.getBoundingClientRect().width ?? 0;
      const occupiedWidth = leftWidth + rightWidth + workspaceGap * (leftWidth > 0 && rightWidth > 0 ? 2 : 1);
      availableWidth -= occupiedWidth;
    }
    availableWidth = Math.max(STAGE_WIDTH * 0.75, availableWidth);
    const availableHeight = Math.max(STAGE_HEIGHT * 0.75, window.innerHeight - verticalPadding);
    const scale = Math.max(0.75, Math.min(2, availableWidth / STAGE_WIDTH, availableHeight / STAGE_HEIGHT));
    const displayWidth = Math.round(STAGE_WIDTH * scale);
    const displayHeight = Math.round(STAGE_HEIGHT * scale);
    this.frameViewport.style.width = `${displayWidth}px`;
    this.frameViewport.style.height = `${displayHeight}px`;
    this.frame.style.width = `${displayWidth}px`;
    this.frame.style.height = `${displayHeight}px`;
    this.hudLayer.style.width = `${STAGE_WIDTH}px`;
    this.hudLayer.style.height = `${STAGE_HEIGHT}px`;
    this.hudLayer.style.transform = `scale(${scale})`;
    this.overlayLayer.style.width = `${STAGE_WIDTH}px`;
    this.overlayLayer.style.height = `${STAGE_HEIGHT}px`;
    this.overlayLayer.style.transform = `scale(${scale})`;

    const devicePixelRatio = window.devicePixelRatio || 1;
    const renderScale = Math.max(1, Math.ceil(scale * devicePixelRatio));
    const nextCanvasWidth = STAGE_WIDTH * renderScale;
    const nextCanvasHeight = STAGE_HEIGHT * renderScale;
    if (this.canvas.width !== nextCanvasWidth || this.canvas.height !== nextCanvasHeight) {
      this.canvas.width = nextCanvasWidth;
      this.canvas.height = nextCanvasHeight;
    }
    this.canvas.style.width = `${displayWidth}px`;
    this.canvas.style.height = `${displayHeight}px`;
  }

  clearOverlay() {
    this.overlayLayer.innerHTML = "";
  }

  showConverterError(message) {
    if (!this.converterError) {
      return;
    }
    this.converterError.textContent = message;
    this.converterError.hidden = !message;
  }

  convertInputFormat() {
    try {
      this.showConverterError("");
      const value = this.converterField.value.trim();
      const looksLikeEvents = /\b(?:press|rel)\b/.test(value) || /^\d+:\d+/m.test(value);
      this.converterField.value = looksLikeEvents
        ? convertEventsToLegacyInput(value)
        : convertLegacyInputToEvents(value);
    } catch (error) {
      this.showConverterError(error instanceof Error ? error.message : String(error));
    }
  }

  syncStepFrameControl() {
    if (!this.stepGroup || !this.stepFrameButton) {
      return;
    }
    this.stepGroup.hidden = this.simulationFps !== 0;
    this.stepFrameButton.disabled = !this.session || this.session.isPaused();
  }

  updateStats() {
    if (!this.session) {
      this.frameValue.textContent = "--";
      this.vxValue.textContent = "--";
      this.vyValue.textContent = "--";
      this.xValue.textContent = "--";
      this.yValue.textContent = "--";
      this.rotationValue.textContent = "--";
      this.rotationVelocityValue.textContent = "--";
      this.syncStepFrameControl();
      return;
    }
    const telemetry = this.session.getTelemetry();
    this.frameValue.textContent = `${telemetry.frame} (${telemetry.frameTime.toFixed(3)})`;
    this.vxValue.textContent = telemetry.vx.toFixed(3);
    this.vyValue.textContent = telemetry.vy.toFixed(3);
    this.xValue.textContent = telemetry.x.toFixed(3);
    this.yValue.textContent = telemetry.y.toFixed(3);
    this.rotationValue.textContent = telemetry.rotation.toFixed(3);
    this.rotationVelocityValue.textContent = telemetry.rotationVelocity.toFixed(3);
    this.syncStepFrameControl();
  }

  getCurrentSpikesKill() {
    return this.store.state.spikesKill === true;
  }

  persistBruteforceSessions() {
    this.store.state.bruteforceSessions = this.bruteforceSessions.map((session) => session.settings);
    this.store.write();
  }

  handleBruteforceSessionSettingsChange() {
    this.persistBruteforceSessions();
  }

  setBruteforceSessionSettings(sessionId, settings) {
    const session = this.bruteforceSessions.find((entry) => entry.sessionId === sessionId);
    if (!session) {
      return;
    }
    session.settings = settings;
    this.persistBruteforceSessions();
  }

  removeBruteforceSession(session) {
    this.bruteforceSessions = this.bruteforceSessions.filter((entry) => entry !== session);
    session.dispose();
    this.syncBruteforceSessionIds();
    this.persistBruteforceSessions();
    this.updateBruteforceSessionEmptyState();
    this.updateStageScale();
  }

  syncBruteforceSessionIds() {
    for (const [index, session] of this.bruteforceSessions.entries()) {
      session.setSessionId(index + 1);
    }
    this.nextBruteforceSessionId = this.bruteforceSessions.length + 1;
  }

  updateBruteforceSessionEmptyState() {
    this.bruteforceEmptyState.hidden = this.bruteforceSessions.length > 0;
  }

  addBruteforceSession(initialSettings = null) {
    const controller = new BruteforceSessionController({
      sessionId: this.nextBruteforceSessionId,
      initialSettings,
      getLevelId: () => 1,
      getSpikesKill: () => this.getCurrentSpikesKill(),
      onSettingsChange: (sessionId, settings) => this.setBruteforceSessionSettings(sessionId, settings),
      onRemove: (session) => this.removeBruteforceSession(session),
    });
    this.nextBruteforceSessionId += 1;
    this.bruteforceSessions.push(controller);
    this.syncBruteforceSessionIds();
    this.bruteforceSessionList.appendChild(controller.render());
    this.handleBruteforceSessionSettingsChange();
    this.updateBruteforceSessionEmptyState();
    this.updateStageScale();
  }

  restoreBruteforceSessions() {
    const storedSessions = Array.isArray(this.store.state.bruteforceSessions) ? this.store.state.bruteforceSessions : [];
    for (const settings of storedSessions) {
      this.addBruteforceSession(settings);
    }
    this.updateBruteforceSessionEmptyState();
  }

  advanceOneFrame() {
    if (!this.session || this.simulationFps !== 0 || this.session.isPaused()) {
      return;
    }
    this.input.syncFrame(this.session.getRaceFrame());
    this.session.stepFrame();
    this.input.beginFrame();
    this.session.render();
    this.updateStats();
  }

  showMenu() {
    this.session = null;
    this.hudLayer.innerHTML = "";
    this.input.clearRace();
    this.clearOverlay();
    this.updateStats();

    const menu = document.createElement("div");
    menu.className = "menu";

    const title = document.createElement("h1");
    title.textContent = "Red Ball - Tournament Edition";

    const copy = document.createElement("p");
    copy.textContent = "";

    const row = document.createElement("div");
    row.className = "row";

    const play = document.createElement("button");
    play.className = "btn";
    play.textContent = "Play";
    play.addEventListener("click", () => this.startGame(1));
    row.appendChild(play);

    const grid = document.createElement("div");
    grid.className = "level-grid";
    for (const level of levelsData.levels) {
      const button = document.createElement("button");
      button.className = "level-btn";
      button.textContent = String(level.id);
      if (TEMPORARILY_LOCKED_LEVEL_IDS.has(level.id)) {
        button.disabled = true;
        button.title = "Temporarily locked: physics not solved yet.";
      } else {
        button.addEventListener("click", () => this.startGame(level.id));
      }
      grid.appendChild(button);
    }

    const legend = document.createElement("div");
    legend.className = "legend";
    legend.textContent = "Controls: left/right arrows or A/D to move, space/up/W to jump, P pause, R restart, Esc to return to menu.";

    menu.append(title, copy, row, grid, legend);
    this.overlayLayer.appendChild(menu);
  }

  startGame(levelId) {
    this.clearOverlay();
    this.session = new GameSession({
      canvas: this.canvas,
      hudLayer: this.hudLayer,
      store: this.store,
      input: this.input,
      showFinishHitbox: Boolean(this.store.state.showFinishHitbox),
      showBallHitbox: Boolean(this.store.state.showBallHitbox),
      autoAdvanceLevel: this.store.state.autoAdvanceLevel !== false,
      spikesKill: this.store.state.spikesKill === true,
      fixedLevelTransitionTiming: this.store.state.fixedLevelTransitionTiming === true,
      tasDeathWarp: this.store.state.tasDeathWarp === true,
      onExit: () => this.showMenu(),
    });
    this.session.startLevel(levelId);
    this.session.setZoom(this.zoomLevel);
    this.session.setHitboxStrokeWidth(this.hitboxStrokeWidth);
    this.session.setFreeCameraEnabled(this.freeCameraEnabled);
    this.updateStats();
  }
}
